import { computed, unref, type MaybeRef, type Ref } from 'vue'
import { bbCodeToPlaintext } from '#features/bb-code'
import type { Message } from '#entities/message'

type MessagePreviewSize = 'small' | 'medium' | 'large'

const MAX_MESSAGE_PREVIEW_LENGTH_BY_SIZE = {
  small: 50,
  medium: 100,
  large: 150,
}

export function useMessagePreview(
  message: Ref<Message | null | undefined>,
  size: MaybeRef<MessagePreviewSize> = 'medium'
) {
  const messageTextPreview = computed(() => {
    const maxMessagePreviewLength = MAX_MESSAGE_PREVIEW_LENGTH_BY_SIZE[unref(size)]

    const messageVal = message.value
    const text = messageVal?.isBBCode ? bbCodeToPlaintext(messageVal?.text ?? '') : messageVal?.text

    if (!messageVal) return ''
    if (!text) return ''

    if (text.length <= maxMessagePreviewLength) return text

    let messageTextPreview = ''
    const messageWords = text?.split(' ') ?? []
    if (!messageWords.length) return ''

    for (const word of messageWords) {
      let newLength = messageTextPreview.length + word.length
      if (!messageTextPreview) newLength += 1

      if (newLength < maxMessagePreviewLength) {
        if (messageTextPreview.length) messageTextPreview += ' '
        messageTextPreview += word
      } else if (messageTextPreview.length === 0) {
        messageTextPreview += word.slice(0, maxMessagePreviewLength)
      } else {
        break
      }
    }

    messageTextPreview += '...'

    return messageTextPreview
  })

  const messageAttachmentsPreview = computed(() => {
    const messageVal = message.value

    if (!messageVal) return ''
    if (!messageVal.attachments.length) return ''

    let messageAttachmentsPreview = ''
    if (messageTextPreview.value.length) messageAttachmentsPreview += '+'
    messageAttachmentsPreview += messageVal.attachments.length

    messageAttachmentsPreview += ' attachment'
    if (messageVal.attachments.length > 1) messageAttachmentsPreview += 's'

    return messageAttachmentsPreview
  })

  return computed(() => {
    let messagePreviewParts = [messageTextPreview.value, messageAttachmentsPreview.value].filter(Boolean)
    return messagePreviewParts.join(' ')
  })
}
